/**
 * <snap-slider> — native scroll-snap carousel (Swiper replacement).
 *
 * Layout is pure CSS: the [data-snap-track] child is a horizontal scroll
 * container with scroll-snap, its children are the slides. The slides are
 * laid out correctly before (or without) this script, so there's no
 * "JS hasn't run yet" layout shift. Swipe, trackpad and arrow-key scrolling
 * are the browser's own. This script only adds:
 *
 * - Active slide tracking: `.is-active` on the slide nearest the track's
 *   start edge (the last slide once the track is scrolled to the end).
 * - Dots ([data-snap-dot], in slide order) and prev/next buttons
 *   ([data-snap-prev] / [data-snap-next]) — all optional, server-rendered.
 * - An eased programmatic glide (data-glide ms, default 900) for dots and
 *   buttons, rather than the browser's fixed-speed smooth scroll. Not
 *   `data-speed`: assets/global-animations.js treats every [data-speed]
 *   element as a parallax layer.
 * - Mouse drag (touch is already native) with a fling.
 *
 * Methods: goTo(i), next(), prev(), and refresh() — call refresh() after
 * adding, removing or re-ordering slides (or dots) so the element re-reads
 * them and goes back to the first slide.
 *
 * Events, dispatched on the element:
 * - `snap-slider:change` — active slide changed. Fires as soon as the
 *   change happens (mid-scroll), so use it for visual state.
 *   detail: { index, previousIndex, slide }
 * - `snap-slider:settle` — scrolling has stopped. Use it for work that
 *   should wait for the motion to finish (e.g. playing a slide's video).
 *   detail: { index, slide }
 *
 * See dev-logs/motion/snap-slider.md.
 */
(function () {
  if (customElements.get('snap-slider')) return;

  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  // Close to the old Swiper wrapper curve, cubic-bezier(0.2, 0.8, 0.2, 1).
  const easeOutQuart = (t) => 1 - Math.pow(1 - t, 4);

  class SnapSlider extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-snap-track]');
      if (!this.track || this._ready) return;
      this._ready = true;

      this._readSlides();
      this.prevBtn = this.querySelector('[data-snap-prev]');
      this.nextBtn = this.querySelector('[data-snap-next]');
      this.speed = parseInt(this.dataset.glide, 10) || 900;
      this.index = -1;
      this._anim = null;
      this._drag = null;

      this._onScroll = this._onScroll.bind(this);
      this._onScrollEnd = this._onScrollEnd.bind(this);
      this._cancelAnim = this._cancelAnim.bind(this);
      this._onPointerDown = this._onPointerDown.bind(this);
      this._onPointerMove = this._onPointerMove.bind(this);
      this._onPointerUp = this._onPointerUp.bind(this);

      this.track.addEventListener('scroll', this._onScroll, { passive: true });
      if ('onscrollend' in window) {
        this.track.addEventListener('scrollend', this._onScrollEnd);
      }
      // A user scroll input interrupts a programmatic glide.
      this.track.addEventListener('wheel', this._cancelAnim, { passive: true });
      this.track.addEventListener('touchstart', this._cancelAnim, { passive: true });
      this.track.addEventListener('pointerdown', this._onPointerDown);
      this.track.addEventListener('dragstart', (e) => e.preventDefault());

      // Delegated, so dots rendered after startup (refresh()) work too.
      this.addEventListener('click', (e) => {
        const dot = e.target.closest('[data-snap-dot]');
        if (!dot || !this.contains(dot)) return;
        const i = this.dots.indexOf(dot);
        if (i !== -1) this.goTo(i);
      });
      if (this.prevBtn) this.prevBtn.addEventListener('click', () => this.goTo(this.index - 1));
      if (this.nextBtn) this.nextBtn.addEventListener('click', () => this.goTo(this.index + 1));

      // Keep the active slide aligned when the slide width changes
      // (breakpoints change slides-per-view).
      let lastWidth = this.track.clientWidth;
      this._resizeObserver = new ResizeObserver(() => {
        const width = this.track.clientWidth;
        if (width === lastWidth) return;
        lastWidth = width;
        this._cancelAnim();
        this.track.scrollLeft = this._targetFor(Math.max(this.index, 0));
      });
      this._resizeObserver.observe(this.track);

      this._setActive(this._nearestIndex(), true);
      this._emit('snap-slider:settle');
    }

    /** Re-read slides and dots (after they change) and go back to the start. */
    refresh() {
      if (!this._ready) return;
      this._cancelAnim();
      this._readSlides();
      this.index = -1;
      this.track.scrollLeft = 0;
      this._setActive(this._nearestIndex(), true);
      this._emit('snap-slider:settle');
    }

    disconnectedCallback() {
      if (this._resizeObserver) this._resizeObserver.disconnect();
      this._cancelAnim();
    }

    /** Glide to slide i (clamped). */
    goTo(i) {
      const index = Math.max(0, Math.min(i, this.slides.length - 1));
      const to = this._targetFor(index);
      this._cancelAnim();
      this._setActive(index);

      const from = this.track.scrollLeft;
      if (reduceMotion.matches || Math.abs(to - from) < 1) {
        this.track.scrollLeft = to;
        this._emit('snap-slider:settle');
        return;
      }

      // Snap is switched off for the glide, or mandatory snapping would
      // fight every intermediate scrollLeft write.
      this.track.style.scrollSnapType = 'none';
      const start = performance.now();
      const anim = { raf: 0 };
      const frame = (now) => {
        const t = Math.min((now - start) / this.speed, 1);
        this.track.scrollLeft = from + (to - from) * easeOutQuart(t);
        if (t < 1) {
          anim.raf = requestAnimationFrame(frame);
        } else {
          this._anim = null;
          this.track.style.scrollSnapType = '';
          this._emit('snap-slider:settle');
        }
      };
      anim.raf = requestAnimationFrame(frame);
      this._anim = anim;
    }

    next() {
      this.goTo(this.index + 1);
    }

    prev() {
      this.goTo(this.index - 1);
    }

    // ── Internals ──

    _readSlides() {
      this.slides = Array.from(this.track.children);
      this.dots = Array.from(this.querySelectorAll('[data-snap-dot]'));
    }

    _maxScroll() {
      return this.track.scrollWidth - this.track.clientWidth;
    }

    /** scrollLeft that puts slide i at the track's snap edge. */
    _targetFor(i) {
      const slide = this.slides[i];
      if (!slide) return 0;
      const padStart = parseFloat(getComputedStyle(this.track).scrollPaddingInlineStart) || 0;
      const offset =
        slide.getBoundingClientRect().left - this.track.getBoundingClientRect().left + this.track.scrollLeft;
      return Math.max(0, Math.min(offset - padStart, this._maxScroll()));
    }

    _nearestIndex() {
      const pos = this.track.scrollLeft;
      if (this.slides.length > 1 && pos >= this._maxScroll() - 2) return this.slides.length - 1;
      let best = 0;
      let bestDist = Infinity;
      for (let i = 0; i < this.slides.length; i++) {
        const dist = Math.abs(this._targetFor(i) - pos);
        if (dist < bestDist) {
          bestDist = dist;
          best = i;
        }
      }
      return best;
    }

    _setActive(index, silent) {
      if (index === this.index) return;
      const previousIndex = this.index;
      this.index = index;

      this.slides.forEach((slide, i) => slide.classList.toggle('is-active', i === index));
      this.dots.forEach((dot, i) => {
        dot.classList.toggle('is-active', i === index);
        dot.setAttribute('aria-current', i === index ? 'true' : 'false');
      });
      if (this.prevBtn) this.prevBtn.disabled = index === 0;
      if (this.nextBtn) this.nextBtn.disabled = index >= this.slides.length - 1;

      if (!silent) this._emit('snap-slider:change', { previousIndex });
    }

    _emit(name, extra) {
      this.dispatchEvent(
        new CustomEvent(name, {
          detail: Object.assign({ index: this.index, slide: this.slides[this.index] }, extra),
        })
      );
    }

    _onScroll() {
      // During a glide the target is already active; don't flicker through
      // the slides it passes.
      if (!this._anim) this._setActive(this._nearestIndex());
      if (!('onscrollend' in window)) {
        clearTimeout(this._scrollTimer);
        this._scrollTimer = setTimeout(this._onScrollEnd, 120);
      }
    }

    _onScrollEnd() {
      if (this._anim || this._drag) return;
      this._setActive(this._nearestIndex());
      this._emit('snap-slider:settle');
    }

    _cancelAnim() {
      if (!this._anim) return;
      cancelAnimationFrame(this._anim.raf);
      this._anim = null;
      this.track.style.scrollSnapType = '';
    }

    // ── Mouse drag (touch and pen scroll natively) ──

    _onPointerDown(e) {
      if (e.pointerType !== 'mouse' || e.button !== 0) return;
      // Links are fine to drag from (whole-card links are common): a press
      // without movement still clicks, and a real drag swallows its click
      // below. Only form fields keep their own mouse behaviour.
      if (e.target.closest('input, textarea, select, [contenteditable]')) return;
      this._cancelAnim();
      this._drag = {
        id: e.pointerId,
        startX: e.clientX,
        startScroll: this.track.scrollLeft,
        startIndex: this.index,
        moved: false,
        samples: [{ t: e.timeStamp, x: e.clientX }],
      };
      window.addEventListener('pointermove', this._onPointerMove);
      window.addEventListener('pointerup', this._onPointerUp);
      window.addEventListener('pointercancel', this._onPointerUp);
    }

    _onPointerMove(e) {
      const drag = this._drag;
      if (!drag || e.pointerId !== drag.id) return;
      const dx = e.clientX - drag.startX;
      if (!drag.moved) {
        if (Math.abs(dx) < 6) return;
        drag.moved = true;
        this.track.style.scrollSnapType = 'none';
        this.classList.add('is-dragging');
      }
      this.track.scrollLeft = drag.startScroll - dx;
      drag.samples.push({ t: e.timeStamp, x: e.clientX });
      if (drag.samples.length > 6) drag.samples.shift();
    }

    _onPointerUp(e) {
      const drag = this._drag;
      if (!drag || e.pointerId !== drag.id) return;
      window.removeEventListener('pointermove', this._onPointerMove);
      window.removeEventListener('pointerup', this._onPointerUp);
      window.removeEventListener('pointercancel', this._onPointerUp);
      this._drag = null;
      if (!drag.moved) return;

      this.classList.remove('is-dragging');
      // The drag ends with a click on whatever is under the cursor; swallow
      // it. That click (if any) fires in the same task as this pointerup, so
      // drop the listener right after in case the release landed elsewhere.
      const swallow = (ev) => {
        ev.preventDefault();
        ev.stopPropagation();
      };
      this.addEventListener('click', swallow, { capture: true, once: true });
      setTimeout(() => this.removeEventListener('click', swallow, true), 0);

      // Fling: px/ms over the last few samples, ignored if the mouse was held
      // still before release.
      const a = drag.samples[0];
      const b = drag.samples[drag.samples.length - 1];
      const dt = b.t - a.t;
      const velocity = dt > 0 && e.timeStamp - b.t < 80 ? (b.x - a.x) / dt : 0;

      let target = this._nearestIndex();
      const dx = e.clientX - drag.startX;
      if (Math.abs(velocity) > 0.3 || (target === drag.startIndex && Math.abs(dx) > 40)) {
        target = drag.startIndex + (dx < 0 ? 1 : -1);
      }
      this.goTo(target);
    }
  }

  customElements.define('snap-slider', SnapSlider);
})();
